import { Box } from '@mui/material';
import { useRef, useState } from 'react';

type SplashReveal = {
  src: string;
  alt?: string;
  size?: number;
  duration?: number;
};

export default function SplashReveal({ src, alt = '', size = 48, duration = 3000 }: SplashReveal) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const [radius, setRadius] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const animationRef = useRef<number | null>(null);

  const animateRadius = (start: number, end: number) => {
    const startTime = performance.now();

    const step = (time: number) => {
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      setRadius(start + (end - start) * eased);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(step);
      }
    };

    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    animationRef.current = requestAnimationFrame(step);
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setCoords({ x, y });
      setRadius(0);
      setIsHovered(true);
      animateRadius(0, size * 2);
    }
  };

  const handleMouseLeave = () => {
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    setIsHovered(false);
    setRadius(0);
  };

  const mask = isHovered
    ? `radial-gradient(circle at ${coords.x}px ${coords.y}px, white ${radius}px, transparent ${
        radius + 40
      }px)`
    : 'radial-gradient(circle at 0px 0px, transparent 0px, transparent 1px)';

  return (
    <Box
      ref={containerRef}
      sx={{
        width: size,
        height: size,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 1,
        cursor: 'pointer',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* base: image with white-only filter */}
      <Box
        component='img'
        src={src}
        alt={alt}
        sx={{
          width: '100%',
          height: '100%',
          filter: 'brightness(0) invert(1)',
        }}
      />

      {/* masked overlay: original color revealed via radial gradient */}
      <Box
        component='img'
        src={src}
        alt={alt}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          maskImage: mask,
          WebkitMaskImage: mask,
        }}
      />
    </Box>
  );
}
